import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import {
  type ClientMeter,
  type ClientMeterCreate,
  type EnergyType,
  type ExpectedDirections,
  clientsApi,
} from "@/api/clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface MetersSectionProps {
  clientId: string;
  energyType: EnergyType;
  canEdit: boolean;
}

export function MetersSection({ clientId, energyType, canEdit }: MetersSectionProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const meters = useQuery({
    queryKey: ["client-meters", clientId, energyType],
    queryFn: () => clientsApi.listMeters(clientId, energyType),
  });

  const del = useMutation({
    mutationFn: (meterId: string) => clientsApi.deleteMeter(clientId, meterId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client-meters", clientId] }),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("clients.meters")}</CardTitle>
          {canEdit && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4" /> {t("clients.add_meter")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {meters.data && meters.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="p-2">{t("clients.meter.name")}</th>
                  {energyType === "electricity" && (
                    <>
                      <th className="p-2">A+</th>
                      <th className="p-2">A−</th>
                      <th className="p-2">{t("clients.meter.expected_directions")}</th>
                    </>
                  )}
                  <th className="p-2">{t("clients.meter.source_id")}</th>
                  {canEdit && <th className="p-2 w-12" />}
                </tr>
              </thead>
              <tbody>
                {meters.data.map((m) => (
                  <MeterRow key={m.id} meter={m} clientId={clientId} canEdit={canEdit}
                    onDelete={() => {
                      if (window.confirm(t("clients.delete_meter_confirm"))) del.mutate(m.id);
                    }} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-slate-500">{t("clients.no_meters")}</div>
        )}
      </CardContent>

      {showAdd && (
        <AddMeterModal
          clientId={clientId}
          energyType={energyType}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            void qc.invalidateQueries({ queryKey: ["client-meters", clientId] });
          }}
        />
      )}
    </Card>
  );
}

// ---------- Row with inline edit of expected_directions ----------

function MeterRow({
  meter,
  clientId,
  canEdit,
  onDelete,
}: {
  meter: ClientMeter;
  clientId: string;
  canEdit: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const update = useMutation({
    mutationFn: (dir: ExpectedDirections) =>
      clientsApi.updateMeter(clientId, meter.id, { expected_directions: dir }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client-meters", clientId] }),
  });

  return (
    <tr className="border-t border-slate-100">
      <td className="p-2 font-medium text-slate-800">{meter.name}</td>
      {meter.energy_type === "electricity" && (
        <>
          <td className="p-2">{meter.count_a_plus ? "✓" : "—"}</td>
          <td className="p-2">{meter.count_a_minus ? "✓" : "—"}</td>
          <td className="p-2">
            <select
              className="h-8 rounded border border-slate-300 bg-white text-xs px-2"
              value={meter.expected_directions}
              disabled={!canEdit || update.isPending}
              onChange={(e) => update.mutate(e.target.value as ExpectedDirections)}
            >
              <option value="a_plus_only">{t("clients.meter.expected_a_plus_only")}</option>
              <option value="a_minus_only">{t("clients.meter.expected_a_minus_only")}</option>
              <option value="both">{t("clients.meter.expected_both")}</option>
            </select>
          </td>
        </>
      )}
      <td className="p-2 font-mono text-xs text-slate-500">
        {meter.source_id ?? "—"}
      </td>
      {canEdit && (
        <td className="p-2">
          <Button variant="ghost" size="icon" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </td>
      )}
    </tr>
  );
}

// ---------- Add modal ----------

interface AddMeterModalProps {
  clientId: string;
  energyType: EnergyType;
  onClose: () => void;
  onCreated: () => void;
}

function AddMeterModal({ clientId, energyType, onClose, onCreated }: AddMeterModalProps) {
  const { t } = useTranslation();

  const [form, setForm] = useState<ClientMeterCreate>(
    energyType === "electricity"
      ? {
          name: "",
          energy_type: "electricity",
          count_a_plus: true,
          count_a_minus: false,
          expected_directions: "a_plus_only",
        }
      : {
          name: "",
          energy_type: "heat",
          count_a_plus: null,
          count_a_minus: null,
          expected_directions: "both",
        },
  );

  const create = useMutation({
    mutationFn: () => clientsApi.createMeter(clientId, form),
    onSuccess: () => onCreated(),
  });

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader><CardTitle>{t("clients.add_meter")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t("clients.meter.name")}</Label>
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          {energyType === "electricity" && (
            <>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.count_a_plus ?? false}
                  onChange={(e) => setForm({ ...form, count_a_plus: e.target.checked })} />
                {t("clients.meter.count_a_plus")}
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.count_a_minus ?? false}
                  onChange={(e) => setForm({ ...form, count_a_minus: e.target.checked })} />
                {t("clients.meter.count_a_minus")}
              </label>
              <div className="space-y-1">
                <Label>{t("clients.meter.expected_directions")}</Label>
                <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  value={form.expected_directions}
                  onChange={(e) => setForm({ ...form, expected_directions: e.target.value as ExpectedDirections })}>
                  <option value="a_plus_only">{t("clients.meter.expected_a_plus_only")}</option>
                  <option value="a_minus_only">{t("clients.meter.expected_a_minus_only")}</option>
                  <option value="both">{t("clients.meter.expected_both")}</option>
                </select>
                <p className="text-xs text-slate-500">{t("clients.meter.directions_hint")}</p>
              </div>
            </>
          )}
        </CardContent>
        <div className="flex gap-2 px-6 pb-6">
          <Button onClick={() => create.mutate()} disabled={!form.name.trim() || create.isPending}>
            {create.isPending ? t("common.saving") : t("common.create")}
          </Button>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      </Card>
    </div>
  );
}
